import { createIntl, createIntlCache, IntlErrorCode, type IntlShape } from "@formatjs/intl";
import type { FormatXMLElementFn, PrimitiveType } from "intl-messageformat";
import { createContext, Fragment, use, useContext, useMemo, type ReactNode } from "react";
import { baseMessages, defaultLocale, englishShellMessage, loadAreaOnce, type Area, type Catalogue, type MessageKey } from "./catalogue.ts";
import { directionOf, type Direction } from "./direction.ts";
import { createFormatters, type Formatters } from "./format.ts";

/**
 * The provider of FR-015-131: `t()` and `<T/>` over `@formatjs/intl` and
 * `intl-messageformat`, with ICU plurals, selects and ordinals, and the `Intl` formatters
 * of `format.ts`. A missing key formats the English message, never the key.
 */

/** The values an ICU message takes: numbers, strings, dates, and for `<T/>` rich-text tag renderers. */
export type MessageValues = Record<string, PrimitiveType>;
export type RichMessageValues = Record<string, PrimitiveType | ReactNode | FormatXMLElementFn<ReactNode>>;

export interface I18n {
  readonly locale: string;
  readonly dir: Direction;
  readonly t: (key: MessageKey, values?: MessageValues) => string;
  readonly format: Formatters;
  /** The workspace zone, a calendar date's, and the browser zone, every time of day's (FR-015-131), which a view drawn by the hour is drawn in (018 FR-018-282). */
  readonly zones: { readonly workspace: string; readonly browser: string };
}

export interface I18nProviderProps {
  readonly locale?: string;
  /** The loaded catalogue for the locale, over English; `loadCatalogue` produces it. */
  readonly messages?: Catalogue;
  readonly workspaceTimeZone: string;
  readonly browserTimeZone: string;
  readonly children: ReactNode;
}

interface I18nContextValue extends I18n {
  readonly intl: IntlShape<ReactNode>;
  /** The merged catalogue, which a nested `AreaCatalogue` extends with the two zones. */
  readonly messages: Catalogue;
}

const I18nContext = createContext<I18nContextValue | null>(null);

/** The descriptor for a key: its English shell message as the default when it has one, and none for an area key. */
function descriptor(key: MessageKey): { readonly id: MessageKey; readonly defaultMessage?: string } {
  const message = englishShellMessage(key);
  return message === undefined ? { id: key } : { id: key, defaultMessage: message };
}
const cache = createIntlCache();

/**
 * Who hears of a key that rendered as itself: an area key read outside an `AreaCatalogue` of its
 * area, or a key no catalogue defines. Nothing listens in the application; the component suite
 * listens and fails the test that rendered one (`vitest.setup.ts`, 2026-09-28), which is how a
 * screen reading an area it never loads is caught before it ships.
 */
let rawKeyListener: ((key: string) => void) | null = null;

export function onRawKey(listener: ((key: string) => void) | null): void {
  rawKeyListener = listener;
}

/**
 * The values with every entry that holds no value left out: `undefined` and `null`, which the
 * formatter prints as nothing, and `NaN`, which a plural prints as "NaN". A variable handed one of
 * them is then missing, which the formatter reports rather than printing a sentence with a hole in it.
 */
function filled<V>(values: Readonly<Record<string, V>> | undefined): Record<string, V> | undefined {
  if (values === undefined) return undefined;
  return Object.fromEntries(Object.entries(values).filter(([, value]) => value !== undefined && value !== null && !(typeof value === "number" && Number.isNaN(value))));
}

/**
 * A message that could not be formatted, a variable missing or a value of the wrong kind
 * (2026-09-28, after screens showed `{name}` and `NaN`): a development or test build throws, so the
 * screen or the test that formats it fails where it happens. A production build keeps rendering the
 * fallback `@formatjs/intl` draws, the message as written, and reports the fault to the console,
 * which is the only client error reporting the application has; `bun run i18n:params` is the check
 * that keeps one from shipping.
 */
function formatFault(error: Error): void {
  if (import.meta.env.DEV) throw error;
  console.error(error);
}

export function createI18n(options: {
  locale?: string;
  messages?: Catalogue;
  workspaceTimeZone: string;
  browserTimeZone: string;
}): I18nContextValue {
  const locale = options.locale ?? defaultLocale;
  const messages: Record<string, string> = { ...baseMessages(), ...options.messages };
  const intl = createIntl<ReactNode>(
    {
      locale,
      defaultLocale,
      messages,
      onError(error) {
        // A missing translation falls back to English by construction (the base catalogue, and
        // the application's once its entry registered it, is merged beneath every locale, an area's English beneath the area), so a key missing here
        // with no English default renders as the key itself, which the listener hears of; every
        // other error is a message that could not be formatted.
        if (error.code !== IntlErrorCode.MISSING_TRANSLATION) {
          formatFault(error);
          return;
        }
        const missing = error as { readonly descriptor?: { readonly id?: unknown; readonly defaultMessage?: unknown } };
        if (missing.descriptor?.defaultMessage === undefined && typeof missing.descriptor?.id === "string") rawKeyListener?.(missing.descriptor.id);
      },
    },
    cache,
  );
  return {
    intl,
    locale,
    messages,
    zones: { workspace: options.workspaceTimeZone, browser: options.browserTimeZone },
    dir: directionOf(locale),
    t: (key, values) => intl.formatMessage(descriptor(key), filled(values)),
    format: createFormatters({ locale, workspaceTimeZone: options.workspaceTimeZone, browserTimeZone: options.browserTimeZone }),
  };
}

export function I18nProvider({ locale, messages, workspaceTimeZone, browserTimeZone, children }: I18nProviderProps) {
  const value = useMemo(
    () =>
      createI18n({
        ...(locale === undefined ? {} : { locale }),
        ...(messages === undefined ? {} : { messages }),
        workspaceTimeZone,
        browserTimeZone,
      }),
    [locale, messages, workspaceTimeZone, browserTimeZone],
  );
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export interface AreaCatalogueProps {
  readonly area: Area;
  readonly children: ReactNode;
}

/**
 * The screens of a lazily loaded area render inside this: on the area's first load it suspends
 * until the catalogue for the active locale is loaded (with English beneath it), and a load that
 * has settled is read without suspending; either way it provides the
 * parent's messages extended with the area's. The route's `Suspense` fallback, the screen's
 * skeleton, covers the wait, so the area's strings never render as keys.
 */
export function AreaCatalogue({ area, children }: AreaCatalogueProps) {
  const parent = useContext(I18nContext);
  if (parent === null) throw new Error("<AreaCatalogue> must be used inside <I18nProvider>");
  const areaMessages = use(loadAreaOnce(parent.locale, area));
  const value = useMemo(
    () => createI18n({ locale: parent.locale, messages: { ...parent.messages, ...areaMessages }, workspaceTimeZone: parent.zones.workspace, browserTimeZone: parent.zones.browser }),
    [parent, areaMessages],
  );
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18n {
  const value = useContext(I18nContext);
  if (value === null) throw new Error("useI18n must be used inside <I18nProvider>");
  return value;
}

export interface TProps {
  readonly id: MessageKey;
  readonly values?: RichMessageValues;
}

/**
 * Render a message inline; rich-text tags in the message map to the functions in `values`. A message
 * whose values carry an element formats to the list of its parts, which React renders as a list and
 * asks keys for, so each part is keyed by its place in the message (2026-09-21: without this every
 * `<T/>` with an element value logged "Each child in a list should have a unique key prop").
 */
export function T({ id, values }: TProps) {
  const context = useContext(I18nContext);
  if (context === null) throw new Error("<T/> must be used inside <I18nProvider>");
  const parts = context.intl.formatMessage<ReactNode, ReactNode | FormatXMLElementFn<ReactNode>>(descriptor(id), filled(values));
  if (!Array.isArray(parts)) return <>{parts}</>;
  return (
    <>
      {parts.map((part, index) => (
        <Fragment key={index}>{part}</Fragment>
      ))}
    </>
  );
}
